import React, { useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import RedirectUrlFieldsService from "packs/services/convertflow/elements/redirect_url_fields_service";
import FieldPickerPopover from "../shared/FieldPickerPopover";
library.add(fas);

const VariantBuilderSettingTextFields = (props) => {
  const [contextData, builder] = useContext(VariantContextData);

  const paramsMode = props.display_type === "params";
  const parseValue = (value) =>
    paramsMode
      ? RedirectUrlFieldsService.parse(value)
      : RedirectUrlFieldsService.parseRaw(value);
  const serializeValue = (newText, newBadges) =>
    paramsMode
      ? RedirectUrlFieldsService.serialize(newText, newBadges)
      : RedirectUrlFieldsService.serializeRaw(newText, newBadges);

  const [text, setText] = useState(() => parseValue(props.value || "").text);
  const [badges, setBadges] = useState(
    () => parseValue(props.value || "").badges
  );
  const [editingIndex, setEditingIndex] = useState(null);
  const timeoutId = useRef(null);
  const lastEmitted = useRef(props.value || "");
  const textInput = useRef(null);
  const keyInput = useRef(null);

  const variant = contextData.objects.variant;
  const website = contextData.objects.website;

  const funnelFieldsBySlug = {};
  Object.values(variant.fields || {})
    .filter((field) => field?.toBeDeleted !== true)
    .forEach((field) => {
      if (field.slug && field.slug !== "New Field") {
        funnelFieldsBySlug[field.slug] = field;
      }
    });

  function updateSetting(value) {
    lastEmitted.current = value;

    if (props.skip_update !== true) {
      builder.update([
        {
          object_type: props.object_type,
          object_id: props.object_id,
          setting_name: props.setting_name,
          value: value,
          skip_history: props.skip_history,
        },
      ]);
    }

    if (props.callback) {
      props.callback(value, props.value);
    }
  }

  function onTextChange(newText) {
    clearTimeout(timeoutId.current);
    const value = serializeValue(newText, badges);
    timeoutId.current = setTimeout(function () {
      updateSetting(value);
    }, 1000);
  }

  function updateBadges(newBadges) {
    clearTimeout(timeoutId.current);
    setBadges(newBadges);
    updateSetting(
      serializeValue(
        textInput.current ? textInput.current.value : text,
        newBadges
      )
    );
  }

  function addBadge(badge) {
    const planType =
      window.plan_type ||
      website?.plan_type ||
      (typeof document !== "undefined"
        ? document.getElementById("upgrade")?.getAttribute("data-plan-type")
        : null);
    const premiumSite =
      website?.premium === true ||
      website?.premium === "true" ||
      (typeof document !== "undefined" &&
        document.getElementById("upgrade")?.getAttribute(
          "data-website-premium"
        ) == "true");

    if (planType == "free" || !premiumSite) {
      if (typeof window.subscriptions?.upgrade_modal === "function") {
        window.subscriptions.upgrade_modal(
          undefined,
          false,
          badge.type === "score"
            ? "add funnel scores"
            : "add merge tags to your funnels",
          "paid",
          badge.type === "score"
            ? "Triggered feature CTA – Funnel Score"
            : "Triggered feature CTA – Merge Tags"
        );
      }
      return;
    }

    updateBadges([...badges, badge]);
  }

  function removeBadge(index) {
    setEditingIndex(null);
    updateBadges(badges.filter((badge, i) => i !== index));
  }

  function commitKeyEdit(index) {
    if (editingIndex === null || !keyInput.current) return;
    setEditingIndex(null);

    const sanitized = keyInput.current.value
      .replace(/[^a-zA-Z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "");

    const badge = badges[index];
    const autoKey = RedirectUrlFieldsService.autoKey(badge);
    const newKey = sanitized === "" || sanitized === autoKey ? null : sanitized;
    if (newKey === badge.key) return;

    updateBadges(
      badges.map((existing, i) =>
        i === index ? { ...existing, key: newKey } : existing
      )
    );
  }

  function badgeDisplay(badge) {
    if (badge.type === "unknown") {
      return {
        key: "unknown tag",
        ref: null,
        warning: "Unrecognized field tag",
      };
    }

    const autoKey = RedirectUrlFieldsService.autoKey(badge);
    const key = badge.key || autoKey;

    if (
      badge.type === "account_field" &&
      !(website.fields && website.fields[badge.slug]) &&
      !funnelFieldsBySlug[badge.slug]
    ) {
      return {
        key: key,
        ref: badge.slug,
        warning: "This field no longer exists in your account",
      };
    }

    return { key: key, ref: autoKey, tooltip: badgeTooltip(badge) };
  }

  function badgeTooltip(badge) {
    if (badge.type === "score") return "Quiz score";
    if (badge.type === "contact_field") {
      return (
        "Contact field: " +
        RedirectUrlFieldsService.contactLabel(badge.attribute)
      );
    }
    if (badge.type === "account_field") {
      const funnelField = funnelFieldsBySlug[badge.slug];
      return funnelField
        ? "Funnel field: " + (funnelField.label || badge.slug)
        : "Field: " + badge.slug;
    }
    return undefined;
  }

  const scoreAdded = badges.some((badge) => badge.type === "score");
  const excludeContactAttributes = badges
    .filter((badge) => badge.type === "contact_field")
    .map((badge) => badge.attribute);
  const excludeSlugs = badges
    .filter((badge) => badge.type === "account_field")
    .map((badge) => badge.slug);

  useEffect(() => {
    const value = props.value ?? "";
    if (value === lastEmitted.current) return;

    clearTimeout(timeoutId.current);
    const reparsed = parseValue(value);
    lastEmitted.current = value;
    setText(reparsed.text);
    setBadges(reparsed.badges);
    setEditingIndex(null);
    if (textInput.current && document.activeElement !== textInput.current) {
      textInput.current.value = reparsed.text;
    }
  }, [props.object_id, props.last_reset, props.value]);

  return (
    <div className="relative">
      <div
        className="w-full p-3 pr-16 bg-white dark:bg-slate-950 rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-2"
        data-setting-container={props.setting_name}
      >
        <input
          type="text"
          maxLength={props.maxLength}
          setting_name={props.setting_name}
          name={props.setting_name}
          className="w-full bg-transparent text-xl text-black dark:text-white border-none outline-none focus:outline-none focus:ring-0 appearance-none p-0"
          defaultValue={text}
          ref={textInput}
          placeholder={props.placeholder}
          onChange={(e) => onTextChange(e.target.value)}
        />

        {badges.map((badge, index) => {
          const display = badgeDisplay(badge);
          const warningStyle = display.warning
            ? "border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950"
            : "border-slate-200 dark:border-slate-700 text-black dark:text-white bg-slate-100 dark:bg-slate-800";

          const badgeKey =
            badge.type === "unknown"
              ? "unknown-" + badge.raw + "-" + index
              : badge.type + "-" + (badge.slug ?? badge.attribute ?? "score");

          return (
            <span
              key={badgeKey}
              className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg border-solid border-[1px] text-lg whitespace-nowrap ${warningStyle}`}
              title={display.warning || display.tooltip}
              data-badge-key={display.key}
            >
              {paramsMode && badge.type !== "unknown" && (
                <button
                  type="button"
                  className="p-0 border-none bg-transparent text-inherit cursor-pointer opacity-60 hover:opacity-100"
                  aria-label="Edit param name"
                  onClick={() => {
                    setEditingIndex(index === editingIndex ? null : index);
                  }}
                >
                  <FontAwesomeIcon icon="fas fa-pencil" />
                </button>
              )}
              {editingIndex === index ? (
                <React.Fragment>
                  <input
                    type="text"
                    className="w-[90px] bg-transparent text-lg border-none outline-none focus:outline-none focus:ring-0 p-0"
                    defaultValue={display.key || ""}
                    ref={keyInput}
                    autoFocus
                    aria-label="Param name"
                    onBlur={() => commitKeyEdit(index)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitKeyEdit(index);
                      if (e.key === "Escape") setEditingIndex(null);
                    }}
                  />
                  {display.ref && <span>{"={" + display.ref + "}"}</span>}
                </React.Fragment>
              ) : (
                <span>
                  {paramsMode
                    ? (display.key || "?") +
                      (display.ref ? "={" + display.ref + "}" : "")
                    : badge.type === "unknown"
                    ? badge.raw
                    : RedirectUrlFieldsService.buildToken(badge)}
                </span>
              )}
              <button
                type="button"
                className="p-0 border-none bg-transparent text-inherit cursor-pointer opacity-60 hover:opacity-100"
                aria-label="Remove field"
                onClick={() => removeBadge(index)}
              >
                <FontAwesomeIcon icon="fas fa-times" />
              </button>
            </span>
          );
        })}
      </div>

      {paramsMode && badges.length > 0 && (
        <div className="mt-1 text-lg text-slate-500 dark:text-slate-400">
          Field values will be added as URL parameters in query string
        </div>
      )}

      <FieldPickerPopover
        className="contents"
        website={website}
        variant={variant}
        onSelect={addBadge}
        excludeContactAttributes={excludeContactAttributes}
        excludeSlugs={excludeSlugs}
        excludeScore={scoreAdded}
      />
    </div>
  );
};

VariantBuilderSettingTextFields.propTypes = {
  object_type: PropTypes.string.isRequired,
  object_id: PropTypes.number,
  setting_name: PropTypes.string.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  callback: PropTypes.func,
  display_type: PropTypes.oneOf(["params"]),
};

export default VariantBuilderSettingTextFields;
